import {
  deriveTrackName,
  omitDataAttributes,
  pickDataAttributes,
  type DataAttributes,
} from "@design-system/utils";
import { CheckIcon, XIcon } from "lucide-react";
import * as React from "react";

import { Badge } from "../badge";
import { Button } from "../button";
import {
  Command,
  commandDefaultFilter,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../command";
import {
  FieldTrigger,
  FieldTriggerButton,
  FieldTriggerValues,
} from "../field-trigger";
import { Loader } from "../loader";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";
import { Separator } from "../separator";

interface MultiSelectOption {
  label: string;
  value: string;
  icon?: React.ComponentType<{ className?: string }>;
  dataAttributes?: DataAttributes;
}

interface MultiSelectProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "value"
> {
  options: MultiSelectOption[];
  value: string[];
  onValueChange: (value: string[]) => void;
  placeholder: string;
  clearLabel: string;
  removeLabel: string;
  emptyLabel: string;
  searchPlaceholder?: string;
  selectAllLabel?: string;
  clearSelectionLabel?: string;
  modalPopover?: boolean;
  multiLine?: boolean;
  searchable?: boolean;
  closeOnSelect?: boolean;
  countSuffix?: string;
  variant?: "default" | "pill";
  onSearch?: (value: string) => void;
  isLoading?: boolean;
  selectedOptions?: MultiSelectOption[];
  ref?: React.RefObject<HTMLButtonElement | null>;
}

const commandItemClassName =
  "data-[selected=true]:bg-shape-neutral-ghost-background-hover data-[selected=true]:text-shape-neutral-ghost-foreground-default rounded-lg pr-8";

interface RemoveBadgeIconProps extends DataAttributes {
  onRemove: () => void;
  removeLabel: string;
}

function RemoveBadgeIcon({
  onRemove,
  removeLabel,
  ...dataAttributes
}: RemoveBadgeIconProps) {
  return (
    <span
      {...dataAttributes}
      role="button"
      tabIndex={-1}
      aria-label={removeLabel}
      className="text-shape-neutral-ghost-foreground-muted hover:text-shape-neutral-ghost-foreground-default pointer-events-auto -mr-0.5 flex size-3.5 shrink-0 cursor-default! items-center justify-center"
      onClick={(event) => {
        event.stopPropagation();
        event.preventDefault();
        onRemove();
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.stopPropagation();
          event.preventDefault();
          onRemove();
        }
      }}
      onPointerDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <XIcon className="pointer-events-none size-3" />
    </span>
  );
}

function SelectedIndicator({ isSelected }: { isSelected: boolean }) {
  return (
    <span
      className="pointer-events-none absolute right-2 flex size-4 items-center justify-center"
      aria-hidden="true"
    >
      {isSelected && (
        <CheckIcon className="text-shape-neutral-ghost-foreground-default size-4" />
      )}
    </span>
  );
}

function MultiSelect({
  options,
  value,
  onValueChange,
  placeholder,
  searchPlaceholder,
  clearLabel,
  removeLabel,
  emptyLabel,
  selectAllLabel,
  clearSelectionLabel,
  modalPopover = false,
  multiLine = false,
  searchable = true,
  closeOnSelect = false,
  countSuffix,
  variant = "default",
  onSearch,
  isLoading = false,
  selectedOptions,
  className,
  disabled,
  "aria-label": ariaLabel,
  onKeyDown,
  ref,
  ...triggerProps
}: MultiSelectProps) {
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState("");
  const listboxId = React.useId();
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const triggerDataAttributes = pickDataAttributes(triggerProps);

  const handleSearchChange = (search: string) => {
    setSearchValue(search);
    onSearch?.(search);
  };

  const handleOpenChange = (open: boolean) => {
    setIsPopoverOpen(open);
    if (!open && searchValue) handleSearchChange("");
  };

  const findOption = (item: string) =>
    options.find((option) => option.value === item) ??
    selectedOptions?.find((option) => option.value === item);

  const toggleOption = (option: string) => {
    onValueChange(
      value.includes(option)
        ? value.filter((item) => item !== option)
        : [...value, option]
    );
    if (closeOnSelect) handleOpenChange(false);
  };

  const clear = () => {
    onValueChange([]);
    setTimeout(() => {
      triggerRef.current?.focus();
    }, 0);
  };

  const clearProps = variant === "pill" ? {} : { onClear: clear, clearLabel };

  const visibleOptions = onSearch
    ? options
    : options.filter(
        (option) => commandDefaultFilter(option.label, searchValue) > 0
      );

  const isEveryOptionSelected =
    visibleOptions.length > 0 &&
    visibleOptions.every((option) => value.includes(option.value));

  const toggleAll = () => {
    const optionValues = visibleOptions.map((option) => option.value);
    onValueChange(
      isEveryOptionSelected
        ? value.filter((item) => !optionValues.includes(item))
        : [...value, ...optionValues.filter((item) => !value.includes(item))]
    );
  };

  const showSelectAll = !!selectAllLabel && visibleOptions.length > 0;

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace" && !event.currentTarget.value) {
      onValueChange(value.slice(0, -1));
    }
  };

  function renderBadge(item: string, badgeRemoveLabel: string) {
    const option = findOption(item);
    const OptionIcon = option?.icon;

    return (
      <Badge key={item} semantic="neutral" emphasis="default">
        {OptionIcon && <OptionIcon />}
        {option?.label ?? item}
        <RemoveBadgeIcon
          {...pickDataAttributes(option?.dataAttributes ?? {})}
          onRemove={() => toggleOption(item)}
          removeLabel={badgeRemoveLabel}
        />
      </Badge>
    );
  }

  const selectedBadges = value.map((item) => renderBadge(item, removeLabel));

  const countBadge = (
    <Badge semantic="neutral" emphasis="default">
      {value.length}
      {countSuffix ? ` ${countSuffix}` : null}
      <RemoveBadgeIcon
        onRemove={clear}
        removeLabel={clearLabel}
        {...deriveTrackName(triggerDataAttributes, "-clear")}
      />
    </Badge>
  );

  function renderValue() {
    if (value.length === 0) {
      return (
        <span className="text-shape-neutral-outlined-foreground-muted truncate text-sm">
          {placeholder}
        </span>
      );
    }

    if (variant === "pill") {
      return value.length === 1
        ? renderBadge(value[0], clearLabel)
        : countBadge;
    }

    if (multiLine) {
      return selectedBadges;
    }

    return (
      <FieldTriggerValues
        countSuffix={countSuffix}
        labels={value.map((item) => findOption(item)?.label ?? item)}
      >
        {selectedBadges}
      </FieldTriggerValues>
    );
  }

  return (
    <Popover
      open={isPopoverOpen}
      onOpenChange={handleOpenChange}
      modal={modalPopover}
    >
      <FieldTrigger
        hasValue={value.length > 0}
        disabled={disabled}
        {...triggerDataAttributes}
        {...clearProps}
      >
        <PopoverTrigger asChild>
          <FieldTriggerButton
            {...omitDataAttributes(triggerProps)}
            variant={variant}
            ref={(element: HTMLButtonElement | null) => {
              triggerRef.current = element;
              if (ref) {
                ref.current = element;
              }
            }}
            disabled={disabled}
            multiLine={multiLine}
            onKeyDown={(event) => {
              if (
                !isPopoverOpen &&
                (event.key === "ArrowDown" || event.key === "ArrowUp")
              ) {
                event.preventDefault();
                setIsPopoverOpen(true);
              }
              onKeyDown?.(event);
            }}
            aria-expanded={isPopoverOpen}
            aria-haspopup="listbox"
            aria-controls={listboxId}
            aria-label={ariaLabel ?? placeholder}
            className={className}
          >
            {renderValue()}
          </FieldTriggerButton>
        </PopoverTrigger>
      </FieldTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] min-w-70 p-0"
        align="start"
        onEscapeKeyDown={() => setIsPopoverOpen(false)}
        onWheel={(event) => event.stopPropagation()}
        onOpenAutoFocus={(event) => {
          const content = event.currentTarget as HTMLElement | null;
          const input =
            content?.querySelector<HTMLInputElement>("[cmdk-input]");
          if (input) {
            event.preventDefault();
            input.focus();
          }
        }}
        onKeyDown={(event) => {
          if (event.key === " ") {
            const target = event.target as HTMLInputElement;
            if (target.matches?.("[cmdk-input]") && target.value) return;

            event.preventDefault();
            const highlighted = event.currentTarget.querySelector<HTMLElement>(
              "[cmdk-item][data-selected='true']"
            );
            if (highlighted) highlighted.click();
          }
        }}
      >
        <Command
          role="listbox"
          aria-multiselectable="true"
          id={listboxId}
          shouldFilter={false}
        >
          {searchable ? (
            <CommandInput
              placeholder={searchPlaceholder}
              onKeyDown={handleInputKeyDown}
              value={searchValue}
              onValueChange={handleSearchChange}
            />
          ) : (
            <div className="sr-only">
              <CommandInput aria-label={placeholder} />
            </div>
          )}
          <CommandList>
            {isLoading ? (
              <div className="flex justify-center px-2 py-3">
                <Loader />
              </div>
            ) : (
              <>
                <CommandEmpty className="m-2">{emptyLabel}</CommandEmpty>
                {visibleOptions.length > 0 ? (
                  <CommandGroup>
                    {showSelectAll ? (
                      <CommandItem
                        {...deriveTrackName(
                          triggerDataAttributes,
                          "-select-all"
                        )}
                        onSelect={toggleAll}
                        className={commandItemClassName}
                      >
                        <span className="pointer-events-none overflow-hidden text-ellipsis whitespace-nowrap">
                          {selectAllLabel}
                        </span>
                        <SelectedIndicator isSelected={isEveryOptionSelected} />
                      </CommandItem>
                    ) : null}
                    {visibleOptions.map((option) => {
                      const isSelected = value.includes(option.value);
                      const OptionIcon = option.icon;

                      return (
                        <CommandItem
                          key={option.value}
                          // `Command` identifies an item by its text unless told
                          // otherwise, so options sharing a label would share one
                          // cursor and highlight together.
                          value={option.value}
                          {...pickDataAttributes(option.dataAttributes ?? {})}
                          onSelect={() => toggleOption(option.value)}
                          className={commandItemClassName}
                        >
                          {OptionIcon && (
                            <OptionIcon className="text-shape-neutral-ghost-foreground-muted h-4 w-4" />
                          )}
                          <span className="pointer-events-none overflow-hidden text-ellipsis whitespace-nowrap">
                            {option.label}
                          </span>
                          <SelectedIndicator isSelected={isSelected} />
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ) : null}
              </>
            )}
          </CommandList>
        </Command>
        {clearSelectionLabel ? (
          <>
            <Separator />
            <div className="flex flex-col p-1">
              <Button
                type="button"
                variant="ghost"
                size="smaller"
                className="h-8 justify-start"
                disabled={value.length === 0}
                onClick={clear}
                {...deriveTrackName(triggerDataAttributes, "-clear-selection")}
              >
                <XIcon className="size-4" />
                {clearSelectionLabel}
              </Button>
            </div>
          </>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

export { MultiSelect };
export type { MultiSelectOption, MultiSelectProps };
